import {
  Box,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  Scatter,
  Stack,
  TimeXAxis,
  Typography,
  tokens,
  YAxis,
} from '@cast/design-system';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import {
  ChartMetricsRow,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  OomKillTooltip,
  OOMKillChartItem,
  oomKillsColorMap,
} from '@cast/split-me/features/cluster/dashboard/WorkloadEventMetricsSection';
import {
  WorkloadContainerSelect,
  useWorkloadSelectedContainer,
  useWorkloadOOMKillsData,
} from '@cast/split-me/features/cluster/workload-optimization';

import { InventoryResourceMetricBox } from '../../../_components/ui/InventoryResourceMetricBox';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

type OomMetricKey =
  | 'total'
  | 'totalManaged'
  | 'totalUnmanaged'
  | 'unmanaged'
  | 'managed';

const colorMap: Record<OomMetricKey, string> = {
  total: tokens.theme.data.grey900,
  totalManaged: tokens.theme.data.red500,
  totalUnmanaged: tokens.theme.data.red100,
  unmanaged: tokens.theme.data.red100,
  managed: tokens.theme.data.red500,
};

const titleMap: Record<OomMetricKey, string> = {
  total: 'TOTAL OOMKILLS',
  totalManaged: 'TOTAL EVENTS',
  totalUnmanaged: 'TOTAL EVENTS',
  unmanaged: 'UNMANAGED',
  managed: 'MANAGED',
};

const MANAGED_TOOLTIP =
  'OOMKill events when Workload Autoscaler enabled for the workload';
const UNMANAGED_TOOLTIP =
  'OOMKill events when Workload Autoscaler not enabled for the workload';

export const WorkloadOOMKillsChart = () => {
  const { getRange, key, timeZone } = useInventoryWorkloadsTimeRangeSelection();
  const { container } = useWorkloadSelectedContainer();
  const {
    chartData,
    isLoading,
    isFetching,
    isError,
    refetch,
    totals,
    isOnlyManaged,
    isOnlyUnmanaged,
    hasBothEvents,
    getOomKillBucketPreview,
  } = useWorkloadOOMKillsData({
    containerName: container?.name,
    timeRangeSelection: { getRange, timeZone, key },
  });
  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<OomMetricKey>();

  const hasEvents = totals.total > 0;
  const isCardLoading = isLoading || (isError && isFetching);
  // 3 boxes when both events shown, otherwise a single total box
  const metricBoxCount = hasBothEvents ? 3 : 1;

  return (
    <WidgetLayout
      title="OOM kills"
      testId="workload-oom-kills-chart"
      headerEndContent={<WorkloadContainerSelect />}
    >
      {isError && !isFetching ? (
        <FailedToLoad title="Failed to load OOM kills" refresh={refetch} />
      ) : (
        <Stack gap={12} height="100%">
          {isCardLoading ? (
            <ChartMetricsRowSkeleton
              alwaysRow
              gap={16}
              count={metricBoxCount}
              testId="workload-oom-metrics-skeleton"
            />
          ) : hasEvents ? (
            <ChartMetricsRow alwaysRow alignItems="center" gap={16}>
              {isOnlyManaged && (
                <InventoryResourceMetricBox
                  testId="workload-oom-total"
                  dataKey="totalManaged"
                  value={totals.total}
                  colorMap={colorMap}
                  titleMap={titleMap}
                  infoTooltip={MANAGED_TOOLTIP}
                  size="s"
                  roundBy={0}
                />
              )}
              {isOnlyUnmanaged && (
                <>
                  <InventoryResourceMetricBox
                    testId="workload-oom-total"
                    dataKey="totalUnmanaged"
                    value={totals.total}
                    colorMap={colorMap}
                    titleMap={titleMap}
                    infoTooltip={UNMANAGED_TOOLTIP}
                    size="s"
                    roundBy={0}
                  />
                  <Box
                    bgcolor={tokens.theme.bg.structural.surfaceSubtle}
                    borderRadius="4px"
                    px={12}
                    py={8}
                    data-testid="workload-oom-unmanaged-banner"
                  >
                    <Typography variant="P12R" color="textSecondary">
                      Not recorded in the Event log if Workload Autoscaler is
                      not enabled for the workload.
                    </Typography>
                  </Box>
                </>
              )}
              {hasBothEvents && (
                <>
                  <InventoryResourceMetricBox
                    testId="workload-oom-total"
                    dataKey="total"
                    value={totals.total}
                    colorMap={colorMap}
                    titleMap={titleMap}
                    size="s"
                    roundBy={0}
                  />
                  <InventoryResourceMetricBox
                    testId="workload-oom-unmanaged"
                    dataKey="unmanaged"
                    value={totals.unmanaged}
                    colorMap={colorMap}
                    titleMap={titleMap}
                    infoTooltip={UNMANAGED_TOOLTIP}
                    size="s"
                    roundBy={0}
                    toggleMetric={toggleMetric}
                    isMetricDisabled={isMetricDisabled}
                  />
                  <InventoryResourceMetricBox
                    testId="workload-oom-managed"
                    dataKey="managed"
                    value={totals.managed}
                    colorMap={colorMap}
                    titleMap={titleMap}
                    infoTooltip={MANAGED_TOOLTIP}
                    size="s"
                    roundBy={0}
                    toggleMetric={toggleMetric}
                    isMetricDisabled={isMetricDisabled}
                  />
                </>
              )}
            </ChartMetricsRow>
          ) : null}
          <Box flex={1}>
            <ChartContainer
              data={chartData}
              isLoading={isCardLoading}
              testId="workload-oom-kills-chart-container"
              sx={{ height: '100%', p: 16 }}
            >
              <ChartGrid hideVertical />
              {!isMetricDisabled('unmanaged') && (
                <Scatter<OOMKillChartItem>
                  dataKey="unmanagedCount"
                  shapeProps={{
                    shapeColor: oomKillsColorMap.unmanagedCount,
                    shapeSize: 'small',
                  }}
                />
              )}
              {!isMetricDisabled('managed') && (
                <Scatter<OOMKillChartItem>
                  dataKey="managedCount"
                  shapeProps={{
                    shapeColor: oomKillsColorMap.managedCount,
                    shapeSize: 'small',
                  }}
                />
              )}
              <TimeXAxis<OOMKillChartItem>
                unixDataKey="chartTimestampUnix"
                timezone={timeZone}
              />
              <YAxis<OOMKillChartItem> allowDecimals={false} />
              <ChartTooltip<OOMKillChartItem>
                TooltipComponent={(props) => (
                  <OomKillTooltip
                    {...props}
                    timeZone={timeZone}
                    interactive
                    clickable={false}
                    getPreviewWorkloads={getOomKillBucketPreview}
                  />
                )}
              />
            </ChartContainer>
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
